import { useLingui } from "@lingui/react/macro";
import { Check, Copy } from "lucide-react";
import { useEffect, useState } from "react";

import { Tooltip } from "../../base/tooltip";

/**
 * Small inline affordance for copying a cell's value to the clipboard. Renders
 * a brand-primary copy icon with a "Copy to clipboard" tooltip; on click it briefly
 * swaps to a green check as confirmation, then reverts.
 */
export function CopyCellButton({ value }: { value: string }) {
  const { t } = useLingui();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  return (
    <Tooltip content={t`Copy to clipboard`}>
      <button
        type="button"
        aria-label={t`Copy to clipboard`}
        className="text-brand-primary hover:text-brand-primary/80 inline-flex shrink-0 cursor-pointer items-center bg-transparent p-0"
        onClick={async (event) => {
          event.stopPropagation();
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
          } catch (error) {
            // Clipboard can reject (denied permission, insecure context). Don't
            // flash the confirmation, and swallow the rejection so it doesn't
            // surface as an unhandled promise rejection.
            console.warn("Copy to clipboard failed", error);
          }
        }}
      >
        {copied ? (
          <Check size={12} className="text-success-foreground" />
        ) : (
          <Copy size={12} />
        )}
      </button>
    </Tooltip>
  );
}
